import DatePicker from 'components/DatePicker'
import { useTodayStart } from 'hooks/useTodayStart'
import { DebouncedTextField } from 'components/DebouncedTextField/DebouncedTextField'
import PPSelect from 'components/PPSelect'
import PPSwitch from 'components/PPSwitch'
import { startOfDay } from 'date-fns'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import {
  editMultiPosts,
  setComposerGoogleButtonType,
  setComposerGoogleEventOptions,
  setComposerGoogleLink,
  setComposerGoogleOfferOptions,
  setGooglePostType
} from 'services/compose'
import { composerPostSelector } from 'services/compose/selectors'
import { message } from 'services/snackbar/actions'
import { isBetaUserSelector } from 'services/users/selectors'
import { GOOGLE_BUTTON_TYPES } from 'shared/constants'
import {
  ComposerGooglePost,
  GOOGLE_POST_TYPE_EVENT,
  GOOGLE_POST_TYPE_OFFER,
  GOOGLE_POST_TYPE_UPDATE,
  GooglePostButtonType
} from 'shared/types'
import StoreState from 'store/state'
import styles from './CustomizePostBlock.pcss'
import { BRAND_GOOGLE } from 'shared'

// EXPL: hoisted out of the render body — static, and a fresh array every render is churn the
// switch has no way to skip. PPSwitch takes a ReactNode label, so the messages come along.
const POST_TYPE_OPTIONS = [{
  value: GOOGLE_POST_TYPE_UPDATE,
  label: <FormattedMessage id="composer.labels.google.post-type-update" />
}, {
  value: GOOGLE_POST_TYPE_OFFER,
  label: <FormattedMessage id="composer.labels.google.post-type-offer" />
}, {
  value: GOOGLE_POST_TYPE_EVENT,
  label: <FormattedMessage id="composer.labels.google.post-type-event" />
}]

// EXPL: both ends of the schedule carry a time of day that the day pickers never show, so comparing
// the raw instants made the guard turn on values the user cannot see — whether the same day was
// accepted came down to whichever time each side happened to hold. Compare whole days instead, and
// let a start and end on the same day stand: a one-day event or offer is a normal thing to post.
const isDayAfter = (date: Date, other: Date) => startOfDay(date).getTime() > startOfDay(other).getTime()

interface ComposerGoogleOptionsProps {
  postData?: ComposerGooglePost
  align?: 'right' | 'left'
  multipostId?: string
  className?: string
}

export default function ComposerGoogleOptions({
  postData, align, multipostId, children, className
}: React.PropsWithChildren<ComposerGoogleOptionsProps>) {
  const dispatch = useDispatch()
  const isBeta = useSelector(isBetaUserSelector)
  const intl = useIntl()
  const composerPost = useSelector((state: StoreState) => composerPostSelector(state, BRAND_GOOGLE)) as ComposerGooglePost
  const post = postData || composerPost
  const [datePickerResetKey, setDatePickerResetKey] = React.useState(0)
  // EXPL: the pickers are memoized, so the floor has to keep its identity between renders — and
  // still follow the clock across midnight. See the hook.
  const minDate = useTodayStart()

  // EXPL: `link` and `buttonType` live under `update` — that is where the single-post reducers write
  // them and where `createPost`/the bulk create read them from. The multipost branch used to write
  // them flat, and `editMultiPosts` shallow-merges into `post.data`, so the value landed on
  // `data.link` where nothing reads it: the field snapped back once the debounce fired and neither
  // value ever reached the API. Typed through `ComposerGooglePost['update']` so the shape is checked
  // rather than waved through by the cast the reducer signature needs.
  const onLinkChange = React.useCallback((value: string) => {
    if (multipostId) {
      const update: ComposerGooglePost['update'] = { ...post.update, link: value }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { update } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleLink(value))
    }
  }, [dispatch, multipostId, post.update])

  const onBtnTypeChange = React.useCallback((type: GooglePostButtonType | 'NONE') => {
    const value = type === 'NONE' ? undefined : type
    if (multipostId) {
      const update: ComposerGooglePost['update'] = { ...post.update, buttonType: value }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { update } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleButtonType(value))
    }
  }, [dispatch, multipostId, post.update])

  const onEventTitleChange = React.useCallback((value: string) => {
    if (multipostId) {
      const event: ComposerGooglePost['event'] = { ...post.event, title: value }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { event } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleEventOptions({ title: value }))
    }
  }, [dispatch, multipostId, post.event])

  const onEventStartDateChange = React.useCallback((date: Date) => {
    if (isDayAfter(date, new Date(post.event.schedule.endDate))) {
      dispatch(message(intl.formatMessage({ id: 'composer.errors.google.start-after-end' }), 'warning'))
      setDatePickerResetKey(prev => prev + 1)
      return
    }
    if (multipostId) {
      const event: ComposerGooglePost['event'] = {
        ...post.event,
        schedule: { ...post.event.schedule, startDate: date.toISOString() }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { event } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleEventOptions({ startDate: date.toISOString() }))
    }
  }, [dispatch, intl, multipostId, post.event])

  const onEventEndDateChange = React.useCallback((date: Date) => {
    if (isDayAfter(new Date(post.event.schedule.startDate), date)) {
      dispatch(message(intl.formatMessage({ id: 'composer.errors.google.start-after-end' }), 'warning'))
      setDatePickerResetKey(prev => prev + 1)
      return
    }
    if (multipostId) {
      const event: ComposerGooglePost['event'] = {
        ...post.event,
        schedule: { ...post.event.schedule, endDate: date.toISOString() }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { event } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleEventOptions({ endDate: date.toISOString() }))
    }
  }, [dispatch, intl, multipostId, post.event])

  const onOfferTitleChange = React.useCallback((value: string) => {
    if (multipostId) {
      const offer: ComposerGooglePost['offer'] = {
        ...post.offer,
        event: { ...post.offer.event, title: value }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { offer } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleOfferOptions({ title: value }))
    }
  }, [dispatch, multipostId, post.offer])

  const onOfferStartDateChange = React.useCallback((date: Date) => {
    if (isDayAfter(date, new Date(post.offer.event.schedule.endDate))) {
      dispatch(message(intl.formatMessage({ id: 'composer.errors.google.start-after-end' }), 'warning'))
      setDatePickerResetKey(prev => prev + 1)
      return
    }
    if (multipostId) {
      const offer: ComposerGooglePost['offer'] = {
        ...post.offer,
        event: {
          ...post.offer.event,
          schedule: { ...post.offer.event.schedule, startDate: date.toISOString() }
        }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { offer } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleOfferOptions({ startDate: date.toISOString() }))
    }
  }, [dispatch, intl, multipostId, post.offer])

  const onOfferEndDateChange = React.useCallback((date: Date) => {
    if (isDayAfter(new Date(post.offer.event.schedule.startDate), date)) {
      dispatch(message(intl.formatMessage({ id: 'composer.errors.google.start-after-end' }), 'warning'))
      setDatePickerResetKey(prev => prev + 1)
      return
    }
    if (multipostId) {
      const offer: ComposerGooglePost['offer'] = {
        ...post.offer,
        event: {
          ...post.offer.event,
          schedule: { ...post.offer.event.schedule, endDate: date.toISOString() }
        }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { offer } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleOfferOptions({ endDate: date.toISOString() }))
    }
  }, [dispatch, intl, multipostId, post.offer])

  const onOfferCouponCodeChange = React.useCallback((value: string) => {
    if (multipostId) {
      const offer: ComposerGooglePost['offer'] = {
        ...post.offer,
        offer: { ...post.offer.offer, couponCode: value }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { offer } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleOfferOptions({ couponCode: value }))
    }
  }, [dispatch, multipostId, post.offer])

  const onOfferRedeemUrlChange = React.useCallback((value: string) => {
    if (multipostId) {
      const offer: ComposerGooglePost['offer'] = {
        ...post.offer,
        offer: { ...post.offer.offer, redeemOnlineUrl: value }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { offer } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleOfferOptions({ redeemOnlineUrl: value }))
    }
  }, [dispatch, multipostId, post.offer])

  const onOfferTermsChange = React.useCallback((value: string) => {
    if (multipostId) {
      const offer: ComposerGooglePost['offer'] = {
        ...post.offer,
        offer: { ...post.offer.offer, termsConditions: value }
      }
      dispatch(editMultiPosts({
        id: multipostId, network: BRAND_GOOGLE,
        data: { offer } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setComposerGoogleOfferOptions({ termsConditions: value }))
    }
  }, [dispatch, multipostId, post.offer])

  const onPostTypeChange = React.useCallback((postType: string) => {
    if (multipostId) {
      dispatch(editMultiPosts({
        id: multipostId,
        network: BRAND_GOOGLE,
        data: { selectedPostType: postType } as Partial<ComposerGooglePost>
      }))
    } else {
      dispatch(setGooglePostType(postType))
    }
  }, [dispatch, multipostId])

  return (
    <div className={`${styles['g-box']} ${className || ''}`} data-testid="composer-google-options">
      {isBeta && (
        <PPSwitch options={POST_TYPE_OPTIONS} selectedValue={post.selectedPostType} onSelectedValueChange={onPostTypeChange} />
      )}
      {post.selectedPostType === GOOGLE_POST_TYPE_UPDATE && (
        <React.Fragment>
          <div className={styles['option-box']}>
            <DebouncedTextField
              type="text"
              title={intl.formatMessage({ id: 'composer.labels.google.link-placeholder' })}
              placeholder={intl.formatMessage({ id: 'composer.labels.google.link-placeholder' })}
              value={post.update.link}
              onTextChange={onLinkChange}
              className={styles['glink-input']}
            />
          </div>
          <div className={`${styles['option-box']} ${align ? styles[align] : ''}`}>
            <PPSelect
              name={intl.formatMessage({ id: 'composer.labels.google.btn-type' })}
              options={GOOGLE_BUTTON_TYPES}
              selectedValue={post.update.buttonType || 'NONE'}
              className={styles['g-select']}
              onSelectionChange={onBtnTypeChange}
            />
          </div>
        </React.Fragment>
      )}
      {post.selectedPostType === GOOGLE_POST_TYPE_EVENT && (
        <React.Fragment>
          <div className={styles['option-box']}>
            <DebouncedTextField
              type="text"
              title={intl.formatMessage({ id: 'composer.labels.google.event-title' })}
              placeholder={intl.formatMessage({ id: 'composer.labels.google.event-title' })}
              value={post.event.title}
              onTextChange={onEventTitleChange}
              className={styles['glink-input']}
            />
          </div>
          <div className={styles['option-box']}>
            <span className={styles['option-label']}><FormattedMessage id="composer.labels.google.start-date" />:</span>
            <DatePicker
              key={`start-${datePickerResetKey}`}
              value={new Date(post.event.schedule.startDate)}
              onChange={onEventStartDateChange}
              minDate={minDate}
            />
          </div>
          <div className={styles['option-box']}>
            <span className={styles['option-label']}><FormattedMessage id="composer.labels.google.end-date" />:</span>
            <DatePicker
              key={`end-${datePickerResetKey}`}
              value={new Date(post.event.schedule.endDate)}
              onChange={onEventEndDateChange}
              minDate={minDate}
            />
          </div>
        </React.Fragment>
      )}
      {post.selectedPostType === GOOGLE_POST_TYPE_OFFER && (
        <React.Fragment>
          <div className={styles['option-box']}>
            <DebouncedTextField
              type="text"
              title={intl.formatMessage({ id: 'composer.labels.google.offer-title' })}
              placeholder={intl.formatMessage({ id: 'composer.labels.google.offer-title' })}
              value={post.offer.event.title}
              onTextChange={onOfferTitleChange}
              className={styles['glink-input']}
            />
          </div>
          <div className={styles['option-box']}>
            <span className={styles['option-label']}><FormattedMessage id="composer.labels.google.start-date" />:</span>
            <DatePicker
              key={`offer-start-${datePickerResetKey}`}
              value={new Date(post.offer.event.schedule.startDate)}
              onChange={onOfferStartDateChange}
              minDate={minDate}
            />
          </div>
          <div className={styles['option-box']}>
            <span className={styles['option-label']}><FormattedMessage id="composer.labels.google.end-date" />:</span>
            <DatePicker
              key={`offer-end-${datePickerResetKey}`}
              value={new Date(post.offer.event.schedule.endDate)}
              onChange={onOfferEndDateChange}
              minDate={minDate}
            />
          </div>
          <div className={styles['option-box']}>
            <DebouncedTextField
              type="text"
              title={intl.formatMessage({ id: 'composer.labels.google.redeem-url' })}
              placeholder={intl.formatMessage({ id: 'composer.labels.google.redeem-url' })}
              value={post.offer.offer.redeemOnlineUrl}
              onTextChange={onOfferRedeemUrlChange}
              className={styles['glink-input']}
            />
          </div>
          <div className={styles['option-box']}>
            <DebouncedTextField
              type="text"
              title={intl.formatMessage({ id: 'composer.labels.google.coupon-code' })}
              placeholder={intl.formatMessage({ id: 'composer.labels.google.coupon-code-placeholder' })}
              value={post.offer.offer.couponCode || ''}
              onTextChange={onOfferCouponCodeChange}
              className={styles['glink-input']}
            />
          </div>
          <div className={styles['option-box']}>
            <DebouncedTextField
              type="text"
              title={intl.formatMessage({ id: 'composer.labels.google.terms' })}
              multiline
              placeholder={intl.formatMessage({ id: 'composer.labels.google.terms-placeholder' })}
              value={post.offer.offer.termsConditions || ''}
              onTextChange={onOfferTermsChange}
              className={styles['glink-input']}
            />
          </div>
        </React.Fragment>
      )}
      {children}
    </div>
  )
}
